/**
 * A short, redacted trail of the errors this session actually hit.
 *
 * WHY IT EXISTS
 * A support ticket that says "ça ne marche pas" costs two round-trips before
 * anyone knows what broke. Attaching the last few errors turns most of those
 * into zero. This is the collection half; the consent half lives in the support
 * form (#284), which shows the user exactly what is about to be sent and lets
 * them untick it.
 *
 * WHY NOT PATCH `console`
 * `permission-error-filter.handler.ts` already patches `console.error` once, for
 * a different purpose, and a second patch layered on top would capture its own
 * filtering. The Angular `ErrorHandler` is the honest seam: it sees real
 * unhandled errors, and it sees them AFTER the two deliberate filters
 * (permission-denied during an auth transition, chunk-load after a deploy),
 * which are self-healing noise nobody should be asked to report.
 *
 * WHY REDACT HERE AND NOT AT SEND TIME
 * Because the buffer itself is the risk. An error message can carry a tenant's
 * e-mail, an IBAN from a payment form, a phone number from a contact editor. If
 * it is never stored unredacted, no later bug — a debug dialog, a log
 * statement, a future feature reading the trail — can leak it.
 */

/** Kept small on purpose: the useful signal is the last thing that broke. */
const MAX_ENTRIES = 10;

/** Long enough for a stack's first frames, short enough not to be a dump. */
const MAX_ENTRY_LENGTH = 500;

const trail: string[] = [];

/**
 * The last description recorded, WITHOUT its timestamp prefix.
 *
 * Kept separately because the entries in `trail` are timestamped: comparing a
 * fresh description against `trail[trail.length - 1]` compares "boom" against
 * "2026-08-06T…Z boom" and never matches, so the de-duplication below silently
 * did nothing. Its own spec is what caught that.
 */
let lastLine: string | null = null;

/**
 * Mask the personal data that routinely ends up inside an error message.
 *
 * Deliberately over-eager: a redacted string that was harmless costs nothing,
 * an unredacted IBAN in a support ticket is a data-protection incident. The
 * order matters — e-mail before the digit rules, or the local part of an
 * address gets partially eaten first.
 *
 * @param text Raw error text.
 * @returns The same text with e-mails, IBANs and phone-shaped runs masked.
 */
export function redactPersonalData(text: string): string {
  return text
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
    // IBAN: two letters, two check digits, then up to 30 alphanumerics —
    // matched before the generic digit run so it is not shredded into pieces.
    .replace(/\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/gi, '[iban]')
    // Phone-shaped: an optional +, then 8+ digits possibly spaced or dotted.
    .replace(/\+?\d[\d\s.-]{7,}\d/g, '[number]');
}

/**
 * Turn anything that was thrown into one readable, bounded line.
 *
 * @param error The value passed to `ErrorHandler.handleError`.
 * @returns A redacted single-line description.
 */
export function describeError(error: unknown): string {
  let raw: string;
  if (error instanceof Error) {
    // The message plus the first stack frame: enough to locate the code,
    // without shipping a 40-line stack of framework internals.
    const firstFrame = (error.stack ?? '').split('\n')[1]?.trim() ?? '';
    raw = firstFrame ? `${error.name}: ${error.message} — ${firstFrame}` : `${error.name}: ${error.message}`;
  } else if (typeof error === 'string') {
    raw = error;
  } else {
    try {
      raw = JSON.stringify(error);
    } catch {
      raw = String(error);
    }
  }
  return redactPersonalData(raw.replace(/\s+/g, ' ').trim()).slice(0, MAX_ENTRY_LENGTH);
}

/**
 * Record one error into the ring buffer.
 *
 * Never throws: it runs inside the global error handler, and an exception here
 * would replace a real crash with a confusing one.
 *
 * @param error The value passed to `ErrorHandler.handleError`.
 */
export function recordError(error: unknown): void {
  try {
    const line = describeError(error);
    if (!line) return;
    // Consecutive duplicates are the common case (a failing listener retrying);
    // ten copies of one message would push out the nine other things that broke.
    // Identity includes the first stack frame, so the same message thrown from
    // two different call sites stays two entries — that is two problems, not
    // one repeated.
    if (lastLine === line) return;
    lastLine = line;
    trail.push(`${new Date().toISOString()} ${line}`);
    if (trail.length > MAX_ENTRIES) trail.shift();
  } catch {
    // Intentionally silent — see the doc comment.
  }
}

/**
 * Read the trail, oldest first.
 *
 * @returns A copy; the caller cannot mutate the buffer.
 */
export function readErrorTrail(): string[] {
  return [...trail];
}

/** Empty the buffer. Used by the specs, and after a ticket is submitted. */
export function clearErrorTrail(): void {
  trail.length = 0;
  // Without this, the first error after a clear would be swallowed whenever it
  // happened to repeat the last one before it.
  lastLine = null;
}
